import dateFormat from "dateformat"

import { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { dateColumnDef, numberColumnDef } from "../../../core/utils/ag-grid-helper";
import { NotificationModel } from "../../../data/model/notification-model";
import { CheckboxSetFilter } from "../../shared/CheckboxSetFilter"
import isEmpty from "lodash/isEmpty"

export interface NotificationRow extends NotificationModel {
    duplicateIds: number[]
}

export const groupNotifications = (notifications: NotificationModel[]): NotificationRow[] => {

    if (isEmpty(notifications)) {
        return []
    }
    
    const keyGen = (notification: NotificationModel): string => {
        const { message, sourceId, createdAt } = notification
        const day = dateFormat(createdAt, "yyyy-mm-dd")
        return `${message}-${sourceId}-${day}`
    }

    if ( Array.isArray(notifications)) {
        const notificationsMap: Record<string, NotificationModel[]> = {}
        notifications.forEach( notification => {
            const key = keyGen(notification)
            if ( !notificationsMap[key] ) {
                notificationsMap[key] = []
            }
            notificationsMap[key].push(notification)
        })

        const rows: NotificationRow[] = []

        const comparator = (n1: NotificationModel, n2: NotificationModel):number => {
            return Date.parse(n1.createdAt) - Date.parse(n2.createdAt)
        }

        for (const key in notificationsMap) {
            const notifications = notificationsMap[key]
            const mostRecent = notifications.sort(comparator)[0]
            const duplicateIds = notifications.map( n => n.id)
            const row = {
                ...mostRecent,
                ...{duplicateIds: duplicateIds}
            }
            rows.push(row)
        }
        return rows
    }
    else {
        return []
    }
}

// Helper function to convert severity enum to display value (matching typeRenderer logic)
const severityToDisplayValue = (severity: string | undefined): string => {
    if (!severity) {
        return ""
    }
    switch(severity.toUpperCase()) {
        case "INFO": return "Informational";
        default: return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
    }
}

export const typeRenderer = (params: ICellRendererParams) => {
    const notificaton: NotificationModel = params.data

    let { severity, typeId} = notificaton

    if (severity) {
        return severityToDisplayValue(severity)
    } else {
        return typeId
    }
}

// Static options for severity filter (display values as shown to user)
const severityFilterOptions = ["Informational", "Warning", "Error", "Critical"]

// Options generator for severity filter - returns fixed set of display values
const severityOptionsGenerator = () => severityFilterOptions

export const notificationsTableCols: ColDef[] = [
    numberColumnDef({
        headerName: "ID",
        field: "id",
        pinned: "left" as const,
        width: 70,
    }),
    {
        headerName: "Message",
        field: "message",
        width: 360,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
            "white-space": "normal !important",
            "line-height": "150%"
        },
        valueGetter:  (params: ValueGetterParams) => {
            const notification: NotificationRow = params.data
            let subscript =  (notification.duplicateIds?.length > 1) ? ` (${notification.duplicateIds.length})` : ""
            return `${notification.message}${subscript}`
        },
        flex: 1
    },
    {
        headerName: "Instrument Name",
        field: "sourceId",
        width: 200,
        filter: true
    },
    {
        headerName: "Type",
        field: "severity",
        width: 150,
        filter: CheckboxSetFilter,
        filterParams: {
            optionsGenerator: severityOptionsGenerator
        },
        valueGetter: (params: ValueGetterParams) => {
            const notification: NotificationRow = params.data
            return severityToDisplayValue(notification?.severity)
        },
        cellRenderer: typeRenderer
    },
    dateColumnDef({
        headerName: "Created On",
        field: "createdAt",
    }),
    {
        headerName: "Instrument Type",
        field: "instrumentType",
        width: 200,
        filter: true
    }
]
